import React, { useState } from 'react';
import { useApp } from '../../appContext';
import DeficiencyModal from './DeficiencyModal';
import type { DraftConcern, Concern } from '../../types';
import { AREA_TYPES } from '../../types';
import { today, getPeriod, getYear, localId } from '../../utils/scheduling';
import { formatDate } from '../../utils/scheduling';

interface FormState {
  area: string;
  areaType: string;
  redTaped: string;
  highbackInspected: string;
  hasDeficiencies: boolean;
  comment: string;
}

const EMPTY_FORM: FormState = {
  area: '',
  areaType: '',
  redTaped: '',
  highbackInspected: '',
  hasDeficiencies: false,
  comment: '',
};

type RadioOption = 'Yes' | 'No' | 'N/A';

function RadioGroup({
  label,
  name,
  value,
  onChange,
  error,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="flex gap-4">
        {(['Yes', 'No', 'N/A'] as RadioOption[]).map((opt) => (
          <label key={opt} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className="accent-[#4C9E00] w-4 h-4"
            />
            <span className="text-sm text-gray-700">{opt}</span>
          </label>
        ))}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export default function InspectionForm() {
  const {
    navigate,
    selectedLocation,
    setSelectedLocation,
    locations,
    concerns,
    draftConcerns,
    isOnline,
    createInspection,
    createConcern,
    saveDraft,
    saveDraftConcern,
    refreshData,
    currentUser,
    currentSupervisor,
  } = useApp();

  const isEditMode = selectedLocation !== null;

  const [form, setForm] = useState<FormState>(() => {
    if (selectedLocation) {
      return {
        area: selectedLocation.Area,
        areaType: selectedLocation.AreaType,
        redTaped: selectedLocation.AllRedTaped ?? '',
        highbackInspected: '',
        hasDeficiencies: (selectedLocation.NumberOfConcerns ?? 0) > 0,
        comment: selectedLocation.Comment ?? '',
      };
    }
    return EMPTY_FORM;
  });

  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [showDefModal, setShowDefModal] = useState(false);
  const [localConcerns, setLocalConcerns] = useState<DraftConcern[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const inspectorName = currentUser;

  // Unique areas from computed locations (used as datalist suggestions)
  const uniqueAreas = Array.from(new Set(locations.map((l) => l.Area))).sort();

  const set = (field: keyof FormState, value: string | boolean) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: '' }));
  };

  // Existing online concerns for selected location
  const existingConcerns = concerns.filter(
    (c) => c.Area === form.area && c.AreaType === form.areaType
  );

  // Existing offline concerns for selected location
  const offlineConcerns = draftConcerns.filter(
    (c) => c.Area === form.area && c.AreaType === form.areaType
  );

  const displayedConcerns = isOnline ? existingConcerns : offlineConcerns;

  const validate = (): boolean => {
    const errs: Partial<Record<string, string>> = {};
    if (!form.area.trim()) errs.area = 'Location is required';
    if (!form.areaType) errs.areaType = 'Area type is required';
    if (!form.redTaped) errs.redTaped = 'Red tape status is required';
    if (!form.highbackInspected) errs.highbackInspected = 'Highback status is required';
    if (form.hasDeficiencies && localConcerns.length === 0 && displayedConcerns.length === 0) {
      errs.deficiencies = 'At least one deficiency must be added';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddConcern = (
    concernData: Omit<DraftConcern, 'localId' | 'parentDraftId'>
  ) => {
    const newConcern: DraftConcern = {
      ...concernData,
      localId: localId(),
      parentDraftId: undefined,
    };
    setLocalConcerns((prev) => [...prev, newConcern]);
    setErrors((e) => ({ ...e, deficiencies: '' }));
    setShowDefModal(false);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    const inspectionDate = today();
    const year = getYear(inspectionDate);
    const period = getPeriod(inspectionDate);
    const concernCount =
      localConcerns.length + (form.hasDeficiencies ? displayedConcerns.length : 0);

    try {
      if (isOnline) {
        // Online: submit inspection directly — locations update automatically
        await createInspection({
          Area: form.area.trim(),
          AreaType: form.areaType,
          DateInspected: inspectionDate,
          InspectedBy: inspectorName,
          Supervisor: currentSupervisor,
          RedTaped: form.redTaped,
          HighbackInspected: form.highbackInspected,
          NumberOfConcerns: concernCount,
          Comment: form.comment,
          Year: year,
          Period: period,
        });

        for (const lc of localConcerns) {
          await createConcern({
            Area: lc.Area,
            AreaType: lc.AreaType,
            Deficiency: lc.Deficiency,
            RemedialAction: lc.RemedialAction,
            SpadBT: lc.SpadBT,
            Remediated: lc.Remediated,
            RemediatedDate: lc.RemediatedDate,
            Comment: lc.Comment,
            Inspector: inspectorName,
            Supervisor: currentSupervisor,
            InspectionDate: inspectionDate,
            Status: lc.Remediated === 'Yes' ? 'Closed' : 'Open',
          });
        }

        refreshData();
      } else {
        // Offline: save as draft
        const draftId = localId();
        saveDraft({
          localId: draftId,
          savedAt: new Date().toISOString(),
          Area: form.area.trim(),
          AreaType: form.areaType,
          DateInspected: inspectionDate,
          InspectedBy: inspectorName,
          Supervisor: currentSupervisor,
          RedTaped: form.redTaped,
          HighbackInspected: form.highbackInspected,
          NumberOfConcerns: concernCount,
          Comment: form.comment,
          Year: year,
          Period: period,
        });

        for (const lc of localConcerns) {
          saveDraftConcern({ ...lc, parentDraftId: draftId, localId: localId() });
        }
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        setSelectedLocation(null);
        navigate('audit-menu');
      }, 1200);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 flex flex-col items-center gap-4 text-center">
        <div className="w-16 h-16 bg-[#EBF7E0] rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-[#4C9E00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800">
          {isOnline ? 'Inspection Submitted!' : 'Draft Saved!'}
        </h2>
        <p className="text-gray-500 text-sm">
          {isOnline
            ? 'The inspection has been recorded successfully.'
            : 'Your inspection has been saved and will upload when online.'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setSelectedLocation(null);
              navigate('audit-menu');
            }}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#4C9E00]">
              {isEditMode ? 'Inspection: ' + selectedLocation?.Area : 'New Inspection'}
            </h1>
            <p className="text-sm text-gray-500">
              {isOnline ? '🟢 Online — will submit directly' : '🟡 Offline — will save as draft'}
            </p>
          </div>
        </div>

        {/* ── Location ─────────────────────────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location <span className="text-red-500">*</span>
          </label>
          {isEditMode ? (
            <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
              {form.area}
            </div>
          ) : (
            <>
              {/* Combobox: type freely or pick from suggestions */}
              <input
                list="area-suggestions"
                value={form.area}
                onChange={(e) => set('area', e.target.value)}
                placeholder="Type or select a location…"
                className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
                  errors.area ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <datalist id="area-suggestions">
                {uniqueAreas.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            </>
          )}
          {errors.area && <p className="text-red-500 text-xs mt-1">{errors.area}</p>}
        </div>

        {/* ── Area Type ─────────────────────────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Area Type <span className="text-red-500">*</span>
          </label>
          {isEditMode ? (
            <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
              {form.areaType}
            </div>
          ) : (
            <select
              value={form.areaType}
              onChange={(e) => set('areaType', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
                errors.areaType ? 'border-red-400' : 'border-gray-300'
              }`}
            >
              <option value="">Select area type…</option>
              {AREA_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}
          {errors.areaType && <p className="text-red-500 text-xs mt-1">{errors.areaType}</p>}
        </div>

        {/* ── Red Tape question ─────────────────────────────────────────── */}
        <RadioGroup
          label="Have all identified concerns been barricaded / red taped?"
          name="redTaped"
          value={form.redTaped}
          onChange={(v) => set('redTaped', v)}
          error={errors.redTaped}
        />

        {/* ── Highback question ─────────────────────────────────────────── */}
        <RadioGroup
          label="Have highback sections been inspected?"
          name="highbackInspected"
          value={form.highbackInspected}
          onChange={(v) => set('highbackInspected', v)}
          error={errors.highbackInspected}
        />

        {/* ── Deficiencies toggle ───────────────────────────────────────── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => set('hasDeficiencies', !form.hasDeficiencies)}
              className={`w-10 h-6 rounded-full relative transition-all duration-200 ${
                form.hasDeficiencies ? 'bg-[#4C9E00]' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${
                  form.hasDeficiencies ? 'left-5' : 'left-1'
                }`}
              />
            </div>
            <span className="text-sm font-semibold text-gray-700">Deficiencies?</span>
          </label>

          {form.hasDeficiencies && (
            <div className="space-y-3">
              {/* Existing concerns */}
              {displayedConcerns.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Existing Concerns
                  </p>
                  {displayedConcerns.map((c, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-800">{c.Deficiency}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{c.RemedialAction}</p>
                          {c.Comment && <p className="text-gray-400 text-xs mt-1 italic">{c.Comment}</p>}
                        </div>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${
                            c.Remediated === 'Yes' || (c as any).Status === 'Closed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {c.Remediated === 'Yes' || (c as any).Status === 'Closed' ? 'Closed' : 'Open'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* New local concerns */}
              {localConcerns.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    New Deficiencies (this inspection)
                  </p>
                  {localConcerns.map((c) => (
                    <div key={c.localId} className="bg-[#EBF7E0] border border-[#4C9E00]/20 rounded-lg p-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-800">{c.Deficiency}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{c.RemedialAction}</p>
                          <p className="text-gray-400 text-xs mt-0.5">Spad/BT: {c.SpadBT}</p>
                        </div>
                        <button
                          onClick={() =>
                            setLocalConcerns((prev) =>
                              prev.filter((x) => x.localId !== c.localId)
                            )
                          }
                          className="text-red-400 hover:text-red-600 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {errors.deficiencies && (
                <p className="text-red-500 text-xs">{errors.deficiencies}</p>
              )}

              <button
                onClick={() => setShowDefModal(true)}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#4C9E00]/40 text-[#4C9E00] py-2.5 rounded-lg text-sm font-medium hover:border-[#4C9E00] hover:bg-[#EBF7E0] transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Deficiency
              </button>
            </div>
          )}
        </div>

        {/* ── Comments ─────────────────────────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Comments / Observations
          </label>
          <textarea
            value={form.comment}
            onChange={(e) => set('comment', e.target.value)}
            rows={3}
            placeholder="Enter any observations or notes…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
          />
        </div>

        {/* ── Submit button ─────────────────────────────────────────────── */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-[#4C9E00] text-white py-3 rounded-xl font-semibold text-base hover:bg-[#3D8000] transition disabled:opacity-60 flex items-center justify-center gap-2 shadow"
        >
          {submitting ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Submitting…
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M5 13l4 4L19 7" />
              </svg>
              {isOnline ? 'Submit Inspection' : 'Save as Draft'}
            </>
          )}
        </button>
      </div>

      {/* Deficiency modal */}
      {showDefModal && (
        <DeficiencyModal
          area={form.area}
          areaType={form.areaType}
          inspector={inspectorName}
          supervisor={currentSupervisor}
          inspectionDate={today()}
          isOnline={isOnline}
          onSave={handleAddConcern}
          onClose={() => setShowDefModal(false)}
        />
      )}
    </>
  );
}
